import { KeyboardEventHandler, useRef } from 'react';
import { SearchIcon, XmarkCircleIcon } from '../icons/react';
import { Spinner } from '../spinner/spinner';
import { useComposedRefs } from '../utils/use-composed-refs';
import { useControllableState } from '../utils/use-controllable-state';
import { Input, InputProps } from './input';
import { InputIconButton } from './input-icon-button';

type SearchInputProps = Omit<InputProps, 'before' | 'after' | 'value'> & {
  onQueryChange: (value?: string) => void;
  value?: string;
  defaultValue?: string;
  loading?: boolean;
};

export const SearchInput = ({
  value,
  onChange,
  onQueryChange: onQueryChangeProp,
  defaultValue,
  ref,
  loading,
  onKeyDown,
  ...props
}: SearchInputProps) => {
  const [query, onQueryChange] = useControllableState({
    value,
    onChange: onQueryChangeProp,
    defaultValue,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const setRef = useComposedRefs(ref, inputRef);

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Escape' && query) {
      onQueryChange('');
    }

    onKeyDown?.(e);
  };

  return (
    <Input
      ref={setRef}
      value={query}
      onChange={(e) => {
        onQueryChange(e.target.value);
        onChange?.(e);
      }}
      onKeyDown={onInputKeyDown}
      before={loading ? <Spinner /> : <SearchIcon width={20} height={20} />}
      after={
        Number(query?.length) > 0 ? (
          <InputIconButton
            onClick={() => {
              onQueryChange('');
              inputRef.current?.focus();
            }}
          >
            <XmarkCircleIcon />
          </InputIconButton>
        ) : null
      }
      autoComplete="off"
      {...props}
    />
  );
};
